export const START_ATTR = "data-s"
export const END_ATTR = "data-e"

export type SourceRange = { start: number; end: number }

function spanOf(node: Node | null): HTMLElement | null {
  const element = node instanceof HTMLElement ? node : node?.parentElement ?? null
  return element?.closest<HTMLElement>(`[${START_ATTR}]`) ?? null
}

function boundary(element: Element, atEnd: boolean): number | null {
  const spans = element.matches(`[${START_ATTR}]`) ? [element] : [...element.querySelectorAll(`[${START_ATTR}]`)]
  if (!spans.length) return null
  const span = atEnd ? spans[spans.length - 1] : spans[0]
  return Number(span.getAttribute(atEnd ? END_ATTR : START_ATTR))
}

export function sourceOffset(node: Node, offset: number): number | null {
  if (node.nodeType === Node.TEXT_NODE) {
    const span = spanOf(node)
    return span ? Number(span.getAttribute(START_ATTR)) + offset : null
  }
  if (!(node instanceof Element)) return null
  const children = node.childNodes
  if (offset < children.length) {
    const child = children[offset]
    return child instanceof Element ? boundary(child, false) : sourceOffset(child, 0)
  }
  const last = children[children.length - 1]
  if (!last) return boundary(node, true)
  return last instanceof Element ? boundary(last, true) : sourceOffset(last, last.textContent?.length ?? 0)
}

export function selectionRange(root: HTMLElement, selection: Selection | null): SourceRange | null {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null
  const range = selection.getRangeAt(0)
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null
  const start = sourceOffset(range.startContainer, range.startOffset)
  const end = sourceOffset(range.endContainer, range.endOffset)
  if (start === null || end === null) return null
  return start < end ? { start, end } : end < start ? { start: end, end: start } : null
}

export function trimRange(text: string, range: SourceRange): SourceRange | null {
  let { start, end } = range
  while (start < end && /\s/.test(text[start])) start++
  while (end > start && /\s/.test(text[end - 1])) end--
  return end > start ? { start, end } : null
}
